import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MapPin, Move, Check, X, Plus, Trash2 } from 'lucide-react';
import { useUpsertZonePosition, useCreatePlybookZone, useDeletePlybookZonePosition } from '@/hooks/usePlybooks';
import { useToast } from '@/hooks/use-toast';
import type { PlybookZonePosition } from '@/types/plybook';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface PlybookZoneWithPosition {
  id: string;
  zone_id: string;
  zone_name: string;
  zone_color: string;
  position_x: number | null;
  position_y: number | null;
}

interface ZoneImageOverlayProps {
  imageUrl: string;
  zonePositions: PlybookZonePosition[];
  plybookId: string;
  selectedZoneId?: string;
  onSelectZone?: (zoneId: string) => void;
  onZonesChange?: () => void;
}

const DEFAULT_COLORS = [
  '#ec4899', // pink
  '#3b82f6', // blue
  '#22c55e', // green
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f97316', // orange
];

export function ZoneImageOverlay({
  imageUrl,
  zonePositions,
  plybookId,
  selectedZoneId,
  onSelectZone,
  onZonesChange,
}: ZoneImageOverlayProps) {
  const { toast } = useToast();
  const upsertPosition = useUpsertZonePosition();
  const createZone = useCreatePlybookZone();
  const deleteZonePosition = useDeletePlybookZonePosition();

  const [positioningZone, setPositioningZone] = useState<PlybookZoneWithPosition | null>(null);
  const [pendingPosition, setPendingPosition] = useState<{ x: number; y: number } | null>(null);
  const [addZoneOpen, setAddZoneOpen] = useState(false);
  const [newZoneName, setNewZoneName] = useState('');
  const [newZoneColor, setNewZoneColor] = useState(DEFAULT_COLORS[0]);

  // Transformar zonePositions a formato más fácil de usar
  const zones: PlybookZoneWithPosition[] = zonePositions.map(zp => ({
    id: zp.id,
    zone_id: zp.zone_id,
    zone_name: zp.zone?.name || 'Sin nombre',
    zone_color: zp.zone?.color || '#6b7280',
    position_x: zp.position_x,
    position_y: zp.position_y,
  }));

  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!positioningZone) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    setPendingPosition({ x, y });
  };

  const handleConfirmPosition = async () => {
    if (!positioningZone || !pendingPosition) return;

    try {
      await upsertPosition.mutateAsync({
        plybookId,
        zoneId: positioningZone.zone_id,
        position_x: pendingPosition.x,
        position_y: pendingPosition.y,
      });
      toast({ title: 'Posición guardada', description: `Zona "${positioningZone.zone_name}" posicionada` });
      setPositioningZone(null);
      setPendingPosition(null);
      onZonesChange?.();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo guardar la posición', variant: 'destructive' });
    }
  };

  const handleCancelPosition = () => {
    setPositioningZone(null);
    setPendingPosition(null);
  };

  const startPositioning = (zone: PlybookZoneWithPosition) => {
    setPositioningZone(zone);
    setPendingPosition(null);
  };

  const handleAddZone = async () => {
    if (!newZoneName.trim()) {
      toast({ title: 'Error', description: 'El nombre de la zona es requerido', variant: 'destructive' });
      return;
    }

    try {
      await createZone.mutateAsync({
        plybookId,
        name: newZoneName.trim(),
        color: newZoneColor,
      });
      toast({ title: 'Zona creada', description: `Zona "${newZoneName}" agregada` });
      setAddZoneOpen(false);
      setNewZoneName('');
      setNewZoneColor(DEFAULT_COLORS[(zones.length + 1) % DEFAULT_COLORS.length]);
      onZonesChange?.();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo crear la zona', variant: 'destructive' });
    }
  };

  const handleDeleteZone = async (zone: PlybookZoneWithPosition) => {
    try {
      await deleteZonePosition.mutateAsync(zone.id);
      toast({ title: 'Zona eliminada', description: `Zona "${zone.zone_name}" eliminada de este plybook` });
      onZonesChange?.();
    } catch (error) {
      toast({ title: 'Error', description: 'No se pudo eliminar la zona', variant: 'destructive' });
    }
  };

  const zonesWithPosition = zones.filter(z =>
    (z.position_x != null && z.position_x > 0) || (z.position_y != null && z.position_y > 0)
  );

  const zonesWithoutPosition = zones.filter(z =>
    (!z.position_x || z.position_x === 0) && (!z.position_y || z.position_y === 0)
  );

  return (
    <>
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Posición de Zonas
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => setAddZoneOpen(true)}>
              <Plus className="w-3 h-3 mr-1" />
              Agregar Zona
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {positioningZone && (
            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-200">
                Posicionando Zona "{positioningZone.zone_name}"
              </p>
              <p className="text-blue-600 dark:text-blue-400">
                {pendingPosition
                  ? 'Haz click en "Confirmar" para guardar o selecciona otra posición'
                  : 'Haz click en la imagen donde va esta zona'}
              </p>
              {pendingPosition && (
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={handleConfirmPosition} disabled={upsertPosition.isPending}>
                    <Check className="w-3 h-3 mr-1" />
                    Confirmar
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleCancelPosition}>
                    <X className="w-3 h-3 mr-1" />
                    Cancelar
                  </Button>
                </div>
              )}
            </div>
          )}

          <div
            className={cn(
              "relative rounded-lg overflow-hidden bg-muted",
              positioningZone && "cursor-crosshair ring-2 ring-blue-500"
            )}
            onClick={handleImageClick}
          >
            <img
              src={imageUrl}
              alt="Plybook"
              className="w-full h-auto"
              draggable={false}
            />

            {zones.map((zone) => {
              const hasPosition = (zone.position_x != null && zone.position_x > 0) || (zone.position_y != null && zone.position_y > 0);
              const isPositioning = positioningZone?.zone_id === zone.zone_id;
              const showAt = isPositioning && pendingPosition
                ? pendingPosition
                : hasPosition
                ? { x: Number(zone.position_x) || 0, y: Number(zone.position_y) || 0 }
                : null;

              if (!showAt) return null;

              return (
                <div
                  key={zone.id}
                  className={cn(
                    "absolute transform -translate-x-1/2 -translate-y-1/2 transition-all",
                    isPositioning && "animate-pulse scale-125"
                  )}
                  style={{
                    left: `${showAt.x}%`,
                    top: `${showAt.y}%`,
                  }}
                >
                  <div
                    className={cn(
                      "px-2 py-1 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg border-2 border-white whitespace-nowrap truncate max-w-[100px]",
                      selectedZoneId === zone.zone_id && "ring-2 ring-offset-2 ring-primary"
                    )}
                    style={{ backgroundColor: zone.zone_color }}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!positioningZone) onSelectZone?.(zone.zone_id);
                    }}
                  >
                    {zone.zone_name}
                  </div>
                </div>
              );
            })}
          </div>

          {zones.length === 0 && (
            <div className="text-center py-4 text-muted-foreground text-sm">
              No hay zonas definidas. Haz click en "Agregar Zona" para crear una.
            </div>
          )}

          {zonesWithoutPosition.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Zonas sin posicionar:</p>
              <div className="flex flex-wrap gap-1">
                {zonesWithoutPosition.map((zone) => {
                  const isPositioning = positioningZone?.zone_id === zone.zone_id;

                  return (
                    <div key={zone.id} className="flex items-center gap-0.5">
                      <Button
                        size="sm"
                        variant={isPositioning ? "default" : "outline"}
                        className="h-7 px-2 text-xs"
                        onClick={() => startPositioning(zone)}
                      >
                        <div
                          className="w-4 h-4 rounded-full mr-1"
                          style={{ backgroundColor: zone.zone_color }}
                        />
                        {zone.zone_name}
                        <Move className="w-3 h-3 ml-1" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteZone(zone)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {zonesWithPosition.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground font-medium">Zonas posicionadas:</p>
              <div className="flex flex-wrap gap-1">
                {zonesWithPosition.map((zone) => {
                  const isPositioning = positioningZone?.zone_id === zone.zone_id;

                  return (
                    <div key={zone.id} className="flex items-center gap-0.5">
                      <Button
                        size="sm"
                        variant={isPositioning ? "default" : "secondary"}
                        className="h-7 px-2 text-xs"
                        onClick={() => startPositioning(zone)}
                      >
                        <div
                          className="w-4 h-4 rounded-full mr-1"
                          style={{ backgroundColor: zone.zone_color }}
                        />
                        {zone.zone_name}
                        <Move className="w-3 h-3 ml-1" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteZone(zone)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={addZoneOpen} onOpenChange={setAddZoneOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Zona</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="zoneName">Nombre de la zona</Label>
              <Input
                id="zoneName"
                value={newZoneName}
                onChange={(e) => setNewZoneName(e.target.value)}
                placeholder="Ej: Zona 1, Borde, Centro..."
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-2">
                {DEFAULT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "w-8 h-8 rounded-full border-2 transition-all",
                      newZoneColor === color ? "border-primary ring-2 ring-primary ring-offset-2" : "border-transparent"
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewZoneColor(color)}
                  />
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Label htmlFor="customColor" className="text-xs">Personalizado:</Label>
                <Input
                  id="customColor"
                  type="color"
                  value={newZoneColor}
                  onChange={(e) => setNewZoneColor(e.target.value)}
                  className="w-12 h-8 p-1 cursor-pointer"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddZoneOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddZone} disabled={createZone.isPending}>
              {createZone.isPending ? 'Creando...' : 'Crear Zona'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
